import { useEffect, useState, useCallback } from "react";
import { api } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Switch } from "./ui/switch";
import { Badge } from "./ui/badge";
import {
  Workflow, RefreshCw, Radio, CheckCircle2, AlertTriangle, ExternalLink
} from "lucide-react";
import { toast } from "sonner";
import { formatTimeAgo } from "../lib/utils";
import { tenantLocale, tenantTz } from "../lib/company";

function fmtErr(detail) {
  if (!detail) return "Request failed";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return String(detail);
}

export default function ServiceNowCard() {
  const [cfg, setCfg] = useState(null);
  const [form, setForm] = useState({
    instance_host: "firstrescue.service-now.com",
    username: "",
    password: "",
    // Blank by default. A hardcoded example here (it used to say "Frosty")
    // gets POSTed straight into a brand-new tenant's config the first time
    // they press Save, handing them another company's task filter.
    assignment_group: "",
    // ON for a tenant with no saved config — see AaWorkformsCard.
    auto_sync: true,
    sync_interval_seconds: 60,
    android_package: "com.servicenow.fulfiller",
    ios_scheme: "snagent",
  });
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [syncing, setSyncing] = useState(false);

  const refresh = useCallback(async (sync = false) => {
    try {
      const { data } = await api.get("/integration/servicenow/config");
      setCfg(data);
      if (sync) {
        setForm((f) => ({
          ...f,
          instance_host: data.instance_host || f.instance_host,
          username: data.username || "",
          assignment_group: data.assignment_group || f.assignment_group,
          auto_sync: (data.username || data.password_set) ? !!data.auto_sync : true,
          sync_interval_seconds: data.sync_interval_seconds || 60,
          android_package: data.android_package || f.android_package,
          ios_scheme: data.ios_scheme || f.ios_scheme,
        }));
      }
    } catch (err) {
      // Polled every 10s — don't surface a toast for transient errors, but log for devs.
      if (process.env.NODE_ENV !== "production") {
        console.debug("[ServiceNowCard] config refresh failed:", err?.message || err);
      }
    }
  }, []);
  useEffect(() => { refresh(true); }, [refresh]);
  useEffect(() => {
    const t = setInterval(() => refresh(false), 10000);
    return () => clearInterval(t);
  }, [refresh]);
  const buildPayload = () => {
    const p = {
      instance_host: form.instance_host.trim(),
      username: form.username.trim(),
      assignment_group: form.assignment_group.trim(),
      auto_sync: form.auto_sync,
      sync_interval_seconds: Math.max(30, parseInt(form.sync_interval_seconds || 60)),
      android_package: form.android_package.trim(),
      ios_scheme: form.ios_scheme.trim().replace(/:\/\/$/, ""),
    };
    if (form.password) p.password = form.password;
    return p;
  };

  // Verify before storing — see AaWorkformsCard.save for the reasoning.
  const save = async () => {
    setSaving(true);
    try {
      const payload = buildPayload();
      try {
        await api.post("/integration/servicenow/test", payload);
      } catch (e) {
        toast.error(`ServiceNow details not saved — ${fmtErr(e.response?.data?.detail)}`, { duration: 10000 });
        return;
      }
      const { data } = await api.put("/integration/servicenow/config", payload);
      setCfg(data);
      setForm((f) => ({ ...f, password: "" }));
      toast.success("ServiceNow settings saved");
    } catch (e) {
      toast.error(fmtErr(e.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const test = async () => {
    setTesting(true);
    try {
      const { data } = await api.post("/integration/servicenow/test", buildPayload());
      toast.success(`ServiceNow OK — wm_task accessible (${data.sample_count} sample row${data.sample_count === 1 ? "" : "s"}).`);
      refresh(false);
      setForm((f) => ({ ...f, password: "" }));
    } catch (e) {
      toast.error(fmtErr(e.response?.data?.detail));
    } finally { setTesting(false); }
  };

  const syncNow = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post("/integration/servicenow/sync");
      toast.success(`Synced ${data.tasks_synced} task${data.tasks_synced === 1 ? "" : "s"} · ${data.created} new · ${data.updated} updated`);
      refresh(false);
      window.dispatchEvent(new Event("jobs:refresh"));
    } catch (e) {
      toast.error(fmtErr(e.response?.data?.detail));
    } finally { setSyncing(false); }
  };

  const statusOk = cfg?.last_sync_status === "ok";
  const statusErr = cfg?.last_sync_status === "error";

  return (
    <section className="bg-white border border-slate-200 rounded-md overflow-hidden" data-testid="servicenow-card">
      <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between bg-gradient-to-r from-indigo-950 to-slate-900 text-white">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-md bg-indigo-600 flex items-center justify-center">
            <Workflow className="h-5 w-5 text-white" strokeWidth={2.5} />
          </div>
          <div>
            <div className="font-display font-bold text-lg">ServiceNow</div>
            <div className="text-xs text-slate-400">
              First Rescue / Field Service Mobile ·{" "}
              <a
                href="https://firstrescue.service-now.com/fsmcp"
                target="_blank" rel="noopener noreferrer"
                className="text-indigo-300 hover:underline inline-flex items-center gap-0.5"
              >fsmcp portal <ExternalLink className="h-3 w-3" /></a>
            </div>
          </div>
        </div>
        <div>
          {cfg?.last_sync_at ? (
            statusOk ? (
              <Badge className="bg-emerald-500/20 text-emerald-300 border-0" data-testid="sn-badge-ok">
                <CheckCircle2 className="h-3 w-3 mr-1" /> Synced {formatTimeAgo(cfg.last_sync_at)}
              </Badge>
            ) : (
              <Badge className="bg-rose-500/20 text-rose-300 border-0" data-testid="sn-badge-err">
                <AlertTriangle className="h-3 w-3 mr-1" /> Error
              </Badge>
            )
          ) : (
            <Badge className="bg-slate-500/20 text-slate-300 border-0" data-testid="sn-badge-idle">Idle</Badge>
          )}
        </div>
      </div>

      <div className="p-6 space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="md:col-span-2">
            <Label className="uppercase-label">Instance Host</Label>
            <Input
              value={form.instance_host}
              onChange={(e) => setForm({ ...form, instance_host: e.target.value })}
              placeholder="firstrescue.service-now.com"
              data-testid="sn-input-host"
            />
          </div>
          <div>
            <Label className="uppercase-label">Username (email)</Label>
            <Input
              type="email"
              value={form.username}
              onChange={(e) => setForm({ ...form, username: e.target.value })}
              placeholder="dispatch@yourcompany.co.nz"
              autoComplete="off"
              data-testid="sn-input-username"
            />
          </div>
          <div>
            <Label className="uppercase-label">Password</Label>
            <Input
              type="password"
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              placeholder={cfg?.password_set ? "•••••••••• — leave blank to keep stored" : "Your ServiceNow password"}
              autoComplete="new-password"
              data-testid="sn-input-password"
            />
          </div>
          <div className="md:col-span-2">
            <Label className="uppercase-label">Assignment Group Filter</Label>
            <Input
              value={form.assignment_group}
              onChange={(e) => setForm({ ...form, assignment_group: e.target.value })}
              placeholder="Your group name in First Rescue"
              data-testid="sn-input-group"
            />
            <div className="text-xs text-slate-500 mt-1">
              Optional. Your First Rescue login already only sees your own jobs, so leaving this blank is safe. Fill it in only to narrow things further — and note it must <em>start with</em> your group&apos;s name exactly as First Rescue spells it, or no jobs will come through.
            </div>
          </div>
          <div className="flex items-center gap-3 bg-slate-50 border border-slate-200 rounded-md px-4 py-3">
            <Switch
              checked={form.auto_sync}
              onCheckedChange={(v) => setForm({ ...form, auto_sync: v })}
              data-testid="sn-auto-switch"
              id="sn-auto"
            />
            <Label htmlFor="sn-auto" className="cursor-pointer">
              <div className="font-semibold text-slate-900 text-sm">Auto-sync</div>
              <div className="text-xs text-slate-500">Poll ServiceNow on the interval.</div>
            </Label>
          </div>
          <div>
            <Label className="uppercase-label">Sync Interval (seconds)</Label>
            <Input
              type="number" min={30}
              value={form.sync_interval_seconds}
              onChange={(e) => setForm({ ...form, sync_interval_seconds: e.target.value })}
              data-testid="sn-input-interval"
            />
          </div>
          <div>
            <Label className="uppercase-label">Driver app — Android package</Label>
            <Input
              value={form.android_package}
              onChange={(e) => setForm({ ...form, android_package: e.target.value })}
              placeholder="com.servicenow.fulfiller"
              className="font-mono"
              data-testid="sn-input-android-package"
            />
            <div className="text-xs text-slate-500 mt-1">
              &ldquo;Open in First Rescue&rdquo; launches this app to its home screen. ServiceNow Agent = com.servicenow.fulfiller.
            </div>
          </div>
          <div>
            <Label className="uppercase-label">Driver app — iOS scheme</Label>
            <Input
              value={form.ios_scheme}
              onChange={(e) => setForm({ ...form, ios_scheme: e.target.value })}
              placeholder="snagent"
              className="font-mono"
              data-testid="sn-input-ios-scheme"
            />
            <div className="text-xs text-slate-500 mt-1">
              Opened as <span className="font-mono">{(form.ios_scheme || "snagent").replace(/:\/\/$/, "")}://</span> on iPhone. ServiceNow Agent = snagent.
            </div>
          </div>
        </div>

        <div className="flex flex-wrap gap-2 pt-3 border-t border-slate-100">
          <Button onClick={save} disabled={saving} className="bg-slate-900 hover:bg-slate-800 text-white border border-slate-800 dark:bg-transparent dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800 dark:hover:border-slate-500 dark:hover:text-white" data-testid="sn-save-btn">
            {saving ? "Saving…" : "Save Settings"}
          </Button>
          <Button variant="outline" onClick={test} disabled={testing} data-testid="sn-test-btn">
            <Radio className="h-4 w-4.5" />
            {testing ? "Testing…" : "Test Connection"}
          </Button>
          <Button variant="outline" onClick={syncNow} disabled={syncing} data-testid="sn-sync-btn">
            <RefreshCw className={`h-4 w-4 mr-1.5 ${syncing ? "animate-spin" : ""}`} />
            {syncing ? "Syncing…" : "Sync Now"}
          </Button>
        </div>

        {cfg?.last_sync_at && (
          <div
            className={`rounded-md p-3 text-sm border ${
              statusOk ? "bg-emerald-50 border-emerald-200 text-emerald-800"
                      : "bg-rose-50 border-rose-200 text-rose-800"
            }`}
            data-testid="sn-last-sync"
          >
            <div className="font-semibold flex items-center gap-1.5">
              {statusOk ? <CheckCircle2 className="h-4 w-4" /> : <AlertTriangle className="h-4 w-4" />}
              Last sync · {formatTimeAgo(cfg.last_sync_at)} ({new Date(cfg.last_sync_at).toLocaleString(tenantLocale(), { timeZone: tenantTz(), hourCycle: "h23" })})
            </div>
            {statusOk && (
              <div className="text-xs mt-1">
                {cfg.last_tasks_synced} tasks fetched · {cfg.last_jobs_created} new
                {cfg.last_jobs_removed > 0 && ` · ${cfg.last_jobs_removed} auto-removed (closed in ServiceNow)`}
              </div>
            )}
            {statusErr && cfg.last_sync_error && (
              <div className="text-xs mt-1 font-mono">{cfg.last_sync_error}</div>
            )}
          </div>
        )}

        {/* Persistent-TLS diagnostics. SN uses stateless Basic Auth, so
            unlike AA this isn't a safety issue — but reusing a warm
            connection roughly halves per-poll latency. */}
        {cfg?.shared_client_active !== undefined && (
          <div
            className="rounded-md p-3 text-xs border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/40 text-slate-700 dark:text-slate-300 font-mono"
            data-testid="sn-session-diagnostics"
          >
            <div className="flex items-center justify-between">
              <span>Warm connection:</span>
              <span className={cfg.shared_client_active
                ? "text-emerald-700 dark:text-emerald-400 font-semibold"
                : "text-slate-500"
              }>
                {cfg.shared_client_active ? "active" : "cold"}
              </span>
            </div>
            <div className="flex items-center justify-between mt-1">
              <span>Requests since process start:</span>
              <span className="text-slate-800 dark:text-slate-200 font-semibold">
                {cfg.shared_client_request_count ?? 0}
              </span>
            </div>
            <div className="mt-1.5 text-slate-500 dark:text-slate-500 italic">
              Persistent TLS pool — cuts per-request latency by ~55%.
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
